import { ElMessageBox } from 'element-plus'

const ACCOUNT_SETUP_PROMPT_KEY = 'account_setup_prompted'

/**
 * 账号是否需要完善引导：登录名仍为系统临时名（wx_* / UUID）或尚未设置密码。
 * 注意：不含 usernameChangeAllowed（登录名改过后永久锁定 ≠ 需要引导），
 * 已有登录名+密码的用户不打扰。
 */
export function needsAccountSetup(userInfo = {}) {
  return requiresInitialAccountSetup(userInfo)
}

/**
 * 是否仍需完善账号：登录名仍是默认名（wx_xxx）或尚未设置密码。
 * 无明确回跳时强制进完善页；带着具体 href 则直达目标（见 shouldForceAccountSetupOnLogin）。
 */
export function requiresInitialAccountSetup(userInfo = {}) {
  return !!(userInfo.usernameAuto || userInfo.passwordUnset)
}

/**
 * 登录带了明确回跳（非首页）时返回 true。
 * 这类场景不强制跳 /user/password?setup=1，直接去目标页。
 */
export function hasConcreteReturnHref(href) {
  if (typeof href !== 'string') return false
  const pathOnly = href.split('?')[0].split('#')[0]
  if (!pathOnly.startsWith('/') || pathOnly.startsWith('//')) return false
  if (pathOnly === '/') return false
  if (pathOnly === '/login' || pathOnly === '/register') return false
  if (pathOnly === '/user/password' || pathOnly === '/user/account') return false
  if (pathOnly === '/wechatcallback' || pathOnly === '/officialcallback') return false
  return true
}

/** 仅「回首页 / 无明确目标」时，微信登录才强制去完善登录信息 */
export function shouldForceAccountSetupOnLogin(href) {
  return !hasConcreteReturnHref(href)
}

/** 完善登录信息页路由，保留原目标 href，完善完成后回跳 */
export function accountSetupRoute(href = '/') {
  const target = hasConcreteReturnHref(href) ? href : ''
  return {
    path: '/user/password',
    query: { setup: '1', ...(target ? { href: target } : {}) }
  }
}

/** 登录流程已直接跳转完善页时，抑制 App.login 的引导弹窗 */
export function markAccountSetupPrompted() {
  sessionStorage.setItem(ACCOUNT_SETUP_PROMPT_KEY, '1')
}

export function accountSecurityMenuLabel(userInfo = {}) {
  if (userInfo.passwordUnset && (userInfo.usernameAuto || userInfo.usernameChangeAllowed)) {
    return '完善登录信息'
  }
  if (userInfo.passwordUnset) {
    return '设置登录密码'
  }
  if (userInfo.usernameAuto) {
    return '设置登录名'
  }
  return '账号安全'
}

export function syncAccountFlagsToStorage(flags = {}) {
  try {
    const raw = localStorage.getItem('user_info')
    const stored = raw ? JSON.parse(raw) : {}
    Object.assign(stored, {
      username: flags.username ?? stored.username,
      usernameAuto: !!flags.usernameAuto,
      usernameChangeAllowed: !!flags.usernameChangeAllowed,
      usernameCooldownDaysLeft: Number(flags.usernameCooldownDaysLeft || 0),
      passwordUnset: !!flags.passwordUnset
    })
    localStorage.setItem('user_info', JSON.stringify(stored))
    return stored
  } catch {
    return null
  }
}

/**
 * 登录后引导完善登录名与密码（每会话最多弹一次，可取消）。
 * 密码登录（loginType === 'password'）不弹：用户已持有登录名+密码。
 * 微信扫码/OAuth 已处理（强制完善或 deep-link 直达并 markAccountSetupPrompted）时跳过。
 */
export async function promptAccountSetupIfNeeded(router, userInfo = {}, opts = {}) {
  if (opts.loginType === 'password') {
    return false
  }
  if (!needsAccountSetup(userInfo)) {
    return false
  }
  if (sessionStorage.getItem(ACCOUNT_SETUP_PROMPT_KEY) === '1') {
    return false
  }
  // 已在账号安全页则不再弹
  const path = router?.currentRoute?.value?.path || ''
  if (path === '/user/password' || path === '/user/account') {
    return false
  }

  sessionStorage.setItem(ACCOUNT_SETUP_PROMPT_KEY, '1')
  try {
    await ElMessageBox.confirm(
      '建议设置登录名与密码，方便在非微信环境登录；并可绑定真实邮箱以便找回密码。\n\n登录名用于登录，昵称仅用于展示，二者不同。',
      '完善账号安全',
      {
        confirmButtonText: '去设置',
        cancelButtonText: '稍后再说',
        type: 'info',
        distinguishCancelAndClose: true
      }
    )
    await router.push({ path: '/user/password', query: { setup: '1' } })
    return true
  } catch {
    return false
  }
}

export function clearAccountSetupPromptFlag() {
  sessionStorage.removeItem(ACCOUNT_SETUP_PROMPT_KEY)
}
